import { NextPage } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useQuery } from "@tanstack/react-query";
import ArticleCollectionPanels from "@pageContent/ArticleCollectionPanels";
import * as StyledArticleContent from "@pageContent/ArticleContent/ArticleContent.styled";
import ArticleHeader from "@pageContent/ArticleHeader";
import Headline from "@pageContent/Headline";
import OopsPage from "@pageContent/OopsPage";
import PageContentSkeleton from "@pageContent/PageContentSkeleton";
import RichText from "@pageContent/RichText";
import SocialSharing from "@pageContent/SocialSharing";
import SponsoredLabel from "@pageContent/SponsoredLabel";
import UnavailableMessage from "@pageContent/UnavailableMessage";
import ArticleContainer from "@pageContent/common/ArticleContainer";
import { CampaignState, SHARE_THIS_CAMPAIGN, ScreenSizes, tableNames } from "@constants/consts";
import { RootNode } from "@typings/richText";
import DynamoDBUtils from "@utils/common/dynamo";
import { useHostConfig } from "@utils/common/getHostConfig";
import { useNoticeError } from "@utils/common/newRelic";
import usePageView from "@utils/common/usePageView";
import useScreenSize from "@utils/common/useScreenSize";
import { fetchByEndpoint } from "@utils/pageContent/fetchData";
import getCampaignState from "@utils/pageContent/getCampaignState";

const CampaignPage: NextPage = ({ initialCampaign }: { initialCampaign?: Campaign | null }) => {
  const { query } = useRouter();
  const screenSize = useScreenSize();
  const { outletName } = useHostConfig();

  const shouldRenderArticlePanels = screenSize === ScreenSizes.lg || screenSize === ScreenSizes.xl;
  const slug = query.slug;
  const campaignQueryKey = ["campaign", slug];

  const {
    isLoading,
    data: campaign,
    isError,
    error,
  } = useQuery({
    queryKey: campaignQueryKey,
    queryFn: () =>
      fetchByEndpoint<Campaign>(`/api/campaign/${slug}`).then((data) => ({
        ...data,
        campaignStatus: getCampaignState(data.startDate, data.endDate),
      })),

    initialData: initialCampaign
      ? { ...initialCampaign, campaignStatus: getCampaignState(initialCampaign.startDate, initialCampaign.endDate) }
      : undefined,
    enabled: !initialCampaign,
    cacheTime: 60000,
  });

  usePageView({
    isLoading,
    error,
    data: campaign,
  });
  useNoticeError(error, { queryKey: campaignQueryKey.join(",") });

  if (!campaign && !isLoading) return <OopsPage is404={!isError} />;

  if (!campaign || isLoading) return <PageContentSkeleton shouldRenderArticlePanels={shouldRenderArticlePanels} />;
  return (
    <>
      <Head>
        <title>{`${campaign.title} | ${outletName}`}</title>
        <meta property="og:title" content={`${campaign.title} | ${outletName}`} />
        <meta property="og:type" content="website" />
      </Head>
      <ArticleContainer>
        <StyledArticleContent.ArticleGrid>
          <StyledArticleContent.ArticleHeaderWrapper>
            {campaign.campaignStatus !== CampaignState.ACTIVE && (
              <UnavailableMessage campaignStatus={campaign.campaignStatus} />
            )}
            <ArticleHeader {...campaign} />
          </StyledArticleContent.ArticleHeaderWrapper>
          <Headline {...campaign} isCampaign />
          {campaign.sponsor && <SponsoredLabel sponsor={campaign.sponsor} />}
          <RichText richText={campaign.content as RootNode} isSponsored={!!campaign.sponsor} />
          <SocialSharing socialTitle={SHARE_THIS_CAMPAIGN} url={campaign.structuredData?.[0].mainEntityOfPage} />
        </StyledArticleContent.ArticleGrid>
        {shouldRenderArticlePanels && <ArticleCollectionPanels />}
      </ArticleContainer>
    </>
  );
};

CampaignPage.getInitialProps = async ({ req, res, query }) => {
  if (!res || !req) return {};

  const initialCampaign = await DynamoDBUtils.getItemBySlug<Campaign>(tableNames.campaign, query.slug as string);

  return { initialCampaign };
};

export default CampaignPage;
